import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { PARTNER_MODELS } from "@/data/site";
import { EASE, SectionHead } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";

const trackSpot = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const Model = ({ m, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
    onMouseMove={trackSpot}
    className={cn("group relative flex flex-col overflow-hidden rounded-[28px] border bg-ink-800", i === 0 ? "border-volt/30 shadow-[0_0_80px_-30px_rgba(0,240,255,0.45)]" : "border-white/5")}
    data-testid={`partner-model-${m.id}`}
  >
    <div className="spot pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    <div className="relative m-2 h-56 overflow-hidden rounded-[22px] sm:h-64">
      <img src={m.img} alt={m.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-800 via-ink-800/20 to-transparent" />
      <span className={cn("absolute left-4 top-4 rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em]", i === 0 ? "bg-volt text-ink-950" : "bg-ink-950/70 text-slate-200 backdrop-blur-md")}>{m.tag}</span>
    </div>
    <div className="relative z-20 flex flex-1 flex-col p-7 pt-3 sm:p-10 sm:pt-4">
      <h3 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{m.title}</h3>
      <p className={cn("mt-2 font-display text-lg font-medium", i === 0 ? "text-volt" : "text-slate-300")}>{m.lead}</p>
      <ul className="mt-7 flex-1 space-y-4">
        {m.points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-slate-300">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border border-volt/40 bg-volt/10"><Check className="h-3 w-3 text-volt" /></span>
            {p}
          </li>
        ))}
      </ul>
      <Link to={m.to} className={cn("mt-9 h-12 self-start px-6 text-sm", i === 0 ? "btn-primary" : "btn-ghost")} data-testid={`partner-model-cta-${m.id}`}>
        {m.cta} <ArrowRight className="btn-arrow h-4 w-4" />
      </Link>
    </div>
  </motion.article>
);

export const PartnerModels = ({ index = "01" }) => (
  <section className="relative py-28 lg:py-40" data-testid="partner-models-section">
    <div className="container-x">
      <SectionHead index={index} eyebrow="Ways to Partner" title={["Two ways to bring", "entertainment in."]}>
        Partner with Brewer on a revenue share with no upfront equipment cost — or purchase your own machines. Either way, we install and support them.
      </SectionHead>
      <div className="relative mt-16 grid gap-5 lg:grid-cols-2">
        {PARTNER_MODELS.map((m, i) => (
          <Model key={m.id} m={m} i={i} />
        ))}
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-30 hidden h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/10 bg-ink-950 font-mono text-xs font-bold tracking-[0.2em] text-slate-400 lg:grid">OR</div>
      </div>
    </div>
  </section>
);
